<template>
  <BuilderWrapper
    title="Data Download"
    helpful-tip="If enabled, this feature allows users to download the raw data in CSV format for your application. You can control which fields will be included in the download. An additional menu button is added which, when clicked, will download the CSV."
    :show-premium-feature="!featureEnabled"
    :expand-disabled="!featureEnabled"
  >
    <LabelledInput>
      <b-checkbox
        v-if="featureEnabled"
        v-model="enabled"
      >
        Enable User Data Download
      </b-checkbox>
    </LabelledInput>

    <LabelledInput v-if="featureEnabled && enabled && !exclude.includes('settings')">
      <div class="d-flex justify-content-between mb-2">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="selectAll"
        >
          Select all >>
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="selectNone"
        >
          &lt;&lt; Select none
        </b-button>
      </div>

      <DraggableFields
        left-heading="Unused Fields"
        right-heading="Selected Fields"
        :source-fields.sync="fieldMapWithTotal"
        :selected-columns.sync="fields"
      />
    </LabelledInput>
  </BuilderWrapper>
</template>

<script lang="ts">

import { PropType } from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import DraggableFields from '@/components/common/DraggableFields/DraggableFields.vue'
import FeatureSetMixin from '@/mixins/FeatureSetMixin'
import { FeatureKeys } from '@/types'
import {
  DOWNLOAD_CONFIG_KEY,
  CONFIG_ENABLED_KEY,
  DOWNLOAD_FIELDS_KEY,
  AUTO_TOTAL_KEY,
} from '@/constants'

const components = {
  BuilderWrapper,
  LabelledInput,
  DraggableFields,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

@Component({ components })
export default class DownloadSettings extends FeatureSetMixin.extend({ props }) {
  featureKey = FeatureKeys.DATA_DOWNLOAD
  featureEnabled!: boolean

  get enabled () {
    return this.$typedStore.primary.app.config[DOWNLOAD_CONFIG_KEY][CONFIG_ENABLED_KEY]
  }

  set enabled (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [DOWNLOAD_CONFIG_KEY, CONFIG_ENABLED_KEY],
      value,
    })
  }

  get fields () {
    return this.$typedStore.primary.app.config[DOWNLOAD_CONFIG_KEY][DOWNLOAD_FIELDS_KEY]
  }

  set fields (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [DOWNLOAD_CONFIG_KEY, DOWNLOAD_FIELDS_KEY],
      value,
    })
  }

  get fieldMap () {
    return this.$typedStore.primary.app.config.fieldMap
  }

  get fieldMapWithTotal () {
    return [
      ...this.fieldMap,
      { column: AUTO_TOTAL_KEY, name: 'Total (auto-generated)' },
    ]
  }

  selectAll () {
    this.fields = this.fieldMapWithTotal.map(f => f.column)
  }

  selectNone () {
    this.fields = []
  }
}

</script>
